import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useLocation, useNavigate } from 'react-router-dom';
import { database, storage, firestore } from '../../firebase/config';
import { ref, push, onValue, off, serverTimestamp, update, set } from 'firebase/database';
import { ref as storageRef, uploadBytes, getDownloadURL } from 'firebase/storage';
import { doc, getDoc, collection, query, where, getDocs } from 'firebase/firestore';
import { toast } from 'react-hot-toast';
import NewChatModal from './NewChatModal';
import useNotifications from '../../hooks/useNotifications';

// Chat List Component
const ChatList = ({ chats, selectedChat, onSelectChat, currentUserId, onlineUsers, userProfiles }) => {
  const [searchTerm, setSearchTerm] = useState('');

  const filteredChats = chats.filter(chat =>
    chat.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    chat.lastMessage?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const formatTime = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    const now = new Date();
    const diffInHours = (now - date) / (1000 * 60 * 60);
    
    if (diffInHours < 1) {
      const diffInMinutes = Math.floor((now - date) / (1000 * 60));
      return diffInMinutes < 1 ? 'now' : `${diffInMinutes}m ago`;
    } else if (diffInHours < 24) {
      return `${Math.floor(diffInHours)}h ago`;
    } else {
      const diffInDays = Math.floor(diffInHours / 24);
      return diffInDays === 1 ? 'yesterday' : `${diffInDays}d ago`;
    }
  };

  return (
    <div className="bg-white rounded-xl shadow-sm overflow-hidden h-full flex flex-col">
      <div className="p-4 border-b border-gray-200">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-semibold text-gray-800">Messages</h2>
          <div className="flex items-center space-x-2">
            <div className="relative">
              <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
            </div>
            <span className="text-xs text-gray-500">{Object.keys(onlineUsers).length} online</span>
          </div>
        </div>
        <div className="relative">
          <input
            type="text"
            placeholder="Search conversations..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#FF8C00] focus:border-transparent"
          />
          <svg className="absolute left-3 top-2.5 w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
        </div>
      </div>
      
      <div className="flex-1 overflow-y-auto">
        {filteredChats.length === 0 ? (
          <div className="p-6 text-left text-gray-500">
            <svg className="w-12 h-12 mb-3 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
            </svg>
            <p className="text-base font-medium mb-1">No conversations yet</p>
            <p className="text-sm">Start chatting with businesses about job opportunities</p>
          </div>
        ) : (
          filteredChats.map(chat => {
            const isSelected = selectedChat?.id === chat.id;
            const unreadCount = chat.unreadCount?.[currentUserId] || 0;
            const isOnline = onlineUsers[chat.otherUserId];

            const userProfile = onSelectChat.bind(null, chat);
            const otherUserProfile = userProfiles[chat.otherUserId];
            
            return (
              <div
                key={chat.id}
                onClick={() => onSelectChat(chat)}
                className={`p-4 border-b border-gray-100 cursor-pointer hover:bg-slate-50 transition-colors ${
                  isSelected ? 'bg-[#FF8C00]/10 border-r-4 border-r-[#FF8C00]' : ''
                } ${unreadCount > 0 ? 'bg-blue-50' : ''}`}
              >
                <div className="flex items-start space-x-3">
                  <div className="relative flex-shrink-0">
                    <div className="w-12 h-12 rounded-full bg-gray-200 overflow-hidden">
                      {chat.avatar ? (
                        <img src={chat.avatar} alt={chat.name} className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-[#FF8C00] to-orange-600 text-white font-semibold text-lg">
                          {chat.name?.charAt(0)?.toUpperCase() || '?'}
                        </div>
                      )}
                    </div>
                    {isOnline && (
                      <div className="absolute -bottom-1 -right-1 w-4 h-4 bg-green-500 rounded-full border-2 border-white"></div>
                    )}
                  </div>
                  
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between mb-1">
                      <div className="flex items-center space-x-2 flex-1 min-w-0">
                                                  <h3 className={`font-medium truncate text-left ${unreadCount > 0 ? 'text-gray-900' : 'text-gray-700'}`}>
                            {chat.name || 'Unknown User'}
                          </h3>
                        {otherUserProfile?.userType && (
                          <span className={`px-1.5 py-0.5 text-xs font-medium rounded-full ${
                            otherUserProfile.userType === 'Business' 
                              ? 'bg-blue-100 text-blue-800' 
                              : 'bg-green-100 text-green-800'
                          }`}>
                            {otherUserProfile.userType}
                          </span>
                        )}
                      </div>
                      <div className="flex items-center space-x-2">
                        {chat.lastMessageTimestamp && (
                          <span className="text-xs text-gray-500 flex-shrink-0">
                            {formatTime(chat.lastMessageTimestamp)}
                          </span>
                        )}
                        {unreadCount > 0 && (
                          <div className="bg-[#FF8C00] text-white text-xs rounded-full px-2 py-1 min-w-[20px] flex items-center justify-center">
                            {unreadCount > 99 ? '99+' : unreadCount}
                          </div>
                        )}
                      </div>
                    </div>
                    
                    {/* Business info preview */}
                    {otherUserProfile?.business && (
                      <p className="text-xs text-blue-600 truncate mb-1 text-left">
                        🏢 {otherUserProfile.business.name}
                      </p>
                    )}
                    
                    <p className={`text-sm truncate text-left ${unreadCount > 0 ? 'text-gray-600 font-medium' : 'text-gray-500'}`}>
                      {chat.lastMessage || 'No messages yet'}
                    </p>
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

// Message Component
const Message = ({ message, isOwn, senderInfo, onImageClick }) => {
  // Debug log to see message structure
  console.log('Rendering message:', message, 'isOwn:', isOwn, 'senderInfo:', senderInfo);
  
  const formatTime = (timestamp) => {
    if (!timestamp) return '';
    return new Date(timestamp).toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  const renderMessageContent = () => {
    // Get the actual text content from either location
    const messageText = message.text || message.content?.text || '';
    console.log('Message text to render:', messageText); // Debug log
    console.log('Full message object:', message); // Debug log - see entire message structure
    
    // Check for file content in multiple possible locations
    const fileUrl = message.fileUrl || message.content?.mediaUrl || message.content?.fileUrl;
    const fileName = message.fileName || message.content?.fileName;
    const fileType = message.fileType || message.content?.type;
    
    console.log('File info - URL:', fileUrl, 'Name:', fileName, 'Type:', fileType); // Debug log
    
    if (fileUrl) {
      console.log('Rendering file message:', fileUrl, fileType); // Debug log
      if (fileType?.startsWith('image/') || fileType === 'image') {
        return (
          <div className="space-y-2">
            <div 
              className="relative group cursor-pointer"
              onClick={() => onImageClick(fileUrl)}
            >
              <img
                src={fileUrl}
                alt="Shared image"
                className="max-w-xs rounded-lg shadow-sm hover:shadow-md transition-shadow"
                style={{ maxHeight: '200px' }}
                onError={(e) => {
                  console.error('Image failed to load:', fileUrl);
                  e.target.style.display = 'none';
                  // Show error message instead
                  e.target.parentNode.innerHTML = `
                    <div class="p-3 border border-red-200 rounded-lg bg-red-50">
                      <p class="text-sm text-red-600">Image failed to load</p>
                      <p class="text-xs text-red-500">${fileName || 'Unknown file'}</p>
                    </div>
                  `;
                }}
                onLoad={() => {
                  console.log('Image loaded successfully:', fileUrl);
                }}
              />
              <div className="absolute inset-0 bg-black opacity-0 group-hover:opacity-10 transition-opacity rounded-lg"></div>
            </div>
            {messageText && (
              <p className="text-sm">{messageText}</p>
            )}
          </div>
        );
      } else {
        return (
          <div className="space-y-2">
            <a 
              href={fileUrl}
              target="_blank"
              rel="noopener noreferrer"
              download={fileName}
              className="flex items-center space-x-3 p-3 border border-gray-200 rounded-lg bg-slate-50 hover:bg-gray-100 transition-colors cursor-pointer"
              onClick={(e) => {
                console.log('File download clicked:', fileUrl, fileName);
                // If URL is invalid, prevent the click
                if (!fileUrl || (!fileUrl.startsWith('http') && !fileUrl.startsWith('blob:'))) {
                  e.preventDefault();
                  console.error('Invalid file URL:', fileUrl);
                  alert('File URL is invalid or not accessible');
                }
              }}
            >
              <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              <div className="flex-1">
                <p className="text-sm font-medium text-gray-900">{fileName || 'Unknown file'}</p>
                <p className="text-xs text-gray-500">Click to download</p>
                {!fileUrl?.startsWith('http') && (
                  <p className="text-xs text-red-500">File URL may be invalid</p>
                )}
              </div>
              <img 
                src="/asset/ForBusiness/download-svgrepo-com.svg" 
                alt="Download" 
                className="w-5 h-5 text-gray-400"
              />
            </a>
            {messageText && (
              <p className="text-sm">{messageText}</p>
            )}
          </div>
        );
      }
    }
    
    return <p className="text-sm whitespace-pre-wrap">{messageText}</p>;
  };

  return (
    <div className={`flex ${isOwn ? 'justify-end' : 'justify-start'} mb-4`}>
      {!isOwn && (
        <div className="flex-shrink-0 mr-3">
          <div className="w-8 h-8 rounded-full bg-gray-200 overflow-hidden">
            {senderInfo?.avatar ? (
              <img src={senderInfo.avatar} alt={senderInfo.name} className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-gray-400 to-gray-600 text-white font-medium text-xs">
                {senderInfo?.name?.charAt(0)?.toUpperCase() || '?'}
              </div>
            )}
          </div>
        </div>
      )}
      
      <div className={`max-w-xs lg:max-w-md ${isOwn ? 'order-1' : 'order-2'}`}>
        <div
          className={`p-3 rounded-lg shadow-sm ${
            isOwn
              ? 'bg-[#FF8C00] text-white rounded-br-none'
              : 'bg-white text-gray-800 rounded-bl-none border border-gray-200'
          }`}
        >
          <div className="text-left">
            {renderMessageContent()}
          </div>
        </div>
        <div className={`mt-1 text-xs text-gray-500 ${isOwn ? 'text-right' : 'text-left'}`}>
          {formatTime(message.timestamp)}
        </div>
      </div>
    </div>
  );
};

// Main Chat Dashboard Component
const ChatDashboard = () => {
  const { currentUser } = useAuth();
  const { requestNotificationPermission, notificationPermission } = useNotifications();
  const location = useLocation();
  const navigate = useNavigate();
  const [chats, setChats] = useState([]);
  const [selectedChat, setSelectedChat] = useState(null);
  const [messages, setMessages] = useState([]);
  const [messageText, setMessageText] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [userProfiles, setUserProfiles] = useState({});
  const [onlineUsers, setOnlineUsers] = useState({});
  const [isTyping, setIsTyping] = useState(false);
  const [typingUsers, setTypingUsers] = useState({});
  const [selectedImage, setSelectedImage] = useState(null);
  const [showNewChatModal, setShowNewChatModal] = useState(false);
  
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);
  const typingTimeoutRef = useRef(null);

  // Helper function to convert storage path to download URL
  const getImageUrl = async (imagePath) => {
    if (!imagePath) return null;
    
    // If it's already a full URL, return as is
    if (imagePath.startsWith('http://') || imagePath.startsWith('https://')) {
      return imagePath;
    }
    
    try {
      // Convert storage path to download URL
      const imageRef = storageRef(storage, imagePath);
      const downloadURL = await getDownloadURL(imageRef);
      return downloadURL;
    } catch (error) {
      console.error('Error converting storage path to URL:', error);
      return null;
    }
  };

  // Helper function to format address
  const formatAddress = (address) => {
    if (!address) return '';
    
    if (typeof address === 'string') {
      return address;
    }
    
    if (typeof address === 'object') {
      const parts = [];
      if (address.street) parts.push(address.street);
      if (address.city) parts.push(address.city);
      if (address.state) parts.push(address.state);
      return parts.join(', ');
    }
    
    return '';
  };

  // Handle URL parameters for direct chat navigation
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const roomId = searchParams.get('room');
    
    if (roomId && chats.length > 0) {
      const targetChat = chats.find(chat => chat.id === roomId);
      if (targetChat) {
        setSelectedChat(targetChat);
        // Clear the URL parameter
        navigate('/dashboard/chat', { replace: true });
      }
    }
  }, [location.search, chats, navigate]);

  // Scroll to bottom of messages
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Load user profile from Firestore
  const loadUserProfile = useCallback(async (userId) => {
    if (userProfiles[userId]) return userProfiles[userId];
    
    try {
      // First try to get from 'users' collection
      let userData = null;
      const userDoc = await getDoc(doc(firestore, 'users', userId));
      if (userDoc.exists()) {
        userData = userDoc.data();
      }
      
      // Also try to get from 'userProfiles' collection for additional profile data
      let userProfileData = null;
      try {
        const userProfileDoc = await getDoc(doc(firestore, 'userProfiles', userId));
        if (userProfileDoc.exists()) {
          userProfileData = userProfileDoc.data();
        }
      } catch (profileError) {
        console.log('No userProfiles document found for user:', userId);
      }
      
      // Combine data from both sources, with userProfiles taking precedence
      const combinedData = { ...userData, ...userProfileData };
      
      // Load business information if this is a business user
      let businessInfo = null;
      if (combinedData && combinedData.businessId) {
        try {
          const businessDoc = await getDoc(doc(firestore, 'businesses', combinedData.businessId));
          if (businessDoc.exists()) {
            const businessData = businessDoc.data();
            businessInfo = {
              id: combinedData.businessId,
              name: businessData.name,
              description: businessData.description,
              address: businessData.address,
              type: businessData.type || 'Restaurant',
              logo: businessData.businessImage?.url || businessData.logo
            };
          }
        } catch (businessError) {
          console.log('Error loading business info:', businessError);
        }
      }
      
      if (combinedData) {
        // Check multiple possible field names for profile photo
        const avatarPath = combinedData.photoURL || 
                          combinedData.profilePhotoURL || 
                          combinedData.profileImageUrl || 
                          combinedData.profileImagePath || 
                          null;
        
        // Convert storage path to download URL if needed
        const avatarUrl = await getImageUrl(avatarPath);
        
        const profile = {
          name: combinedData.displayName || 
                `${combinedData.firstName || ''} ${combinedData.lastName || ''}`.trim() || 
                'Unknown User',
          avatar: avatarUrl,
          businessId: combinedData.businessId,
          business: businessInfo,
          role: combinedData.role,
          email: combinedData.email,
          userType: combinedData.role === 'business_user' ? 'Business' : 'Job Seeker'
        };
        
        setUserProfiles(prev => ({ ...prev, [userId]: profile }));
        return profile;
      }
    } catch (error) {
      console.error('Error loading user profile:', error);
    }
    return { name: 'Unknown User', avatar: null };
  }, [userProfiles]);

  // Load chats from chatRooms
  useEffect(() => {
    if (!currentUser) return;

    const chatRoomsRef = ref(database, 'chatRooms');
    const unsubscribe = onValue(chatRoomsRef, async (snapshot) => {
      const chatRoomsData = snapshot.val();
      if (!chatRoomsData) {
        setChats([]);
        setLoading(false);
        return;
      }

      const userChats = [];
      for (const [roomId, room] of Object.entries(chatRoomsData)) {
        if (room.participants && room.participants[currentUser.uid]) {
          const otherUserId = Object.keys(room.participants).find(id => id !== currentUser.uid);
          if (otherUserId) {
            const otherUserProfile = await loadUserProfile(otherUserId);
            userChats.push({
              id: roomId,
              name: otherUserProfile.name,
              avatar: otherUserProfile.avatar,
              lastMessage: room.lastMessage || '',
              lastMessageTimestamp: room.lastMessageTimestamp || 0,
              unreadCount: room.unreadCount || {},
              otherUserId,
              participants: room.participants
            });
          }
        }
      }

      // Sort by last message timestamp
      userChats.sort((a, b) => (b.lastMessageTimestamp || 0) - (a.lastMessageTimestamp || 0));
      setChats(userChats);
      setLoading(false);
    });

    return () => off(chatRoomsRef, 'value', unsubscribe);
  }, [currentUser, loadUserProfile]);

  // Monitor online users
  useEffect(() => {
    if (!currentUser) return;

    const onlineRef = ref(database, 'users');
    const unsubscribe = onValue(onlineRef, (snapshot) => {
      const usersData = snapshot.val();
      const online = {};
      
      if (usersData) {
        Object.entries(usersData).forEach(([userId, userData]) => {
          if (userData.presence?.isOnline) {
            online[userId] = true;
          }
        });
      }
      
      setOnlineUsers(online);
    });

    // Set current user as online
    const currentUserPresenceRef = ref(database, `users/${currentUser.uid}/presence`);
    set(currentUserPresenceRef, {
      isOnline: true,
      lastOnline: serverTimestamp()
    });

    // Set offline when user disconnects
    const connectedRef = ref(database, '.info/connected');
    onValue(connectedRef, (snapshot) => {
      if (snapshot.val() === false) {
        return;
      }

      const userPresenceRef = ref(database, `users/${currentUser.uid}/presence`);
      const offlineData = {
        isOnline: false,
        lastOnline: serverTimestamp()
      };
      
      // Set offline status when disconnected
      import('firebase/database').then(({ onDisconnect }) => {
        onDisconnect(userPresenceRef).set(offlineData);
      });
    });

    return () => off(onlineRef, 'value', unsubscribe);
  }, [currentUser]);

  // Load messages for selected chat
  useEffect(() => {
    if (!selectedChat || !currentUser) return;

    const messagesRef = ref(database, `chatRooms/${selectedChat.id}/messages`);
    const unsubscribe = onValue(messagesRef, async (snapshot) => {
      const messagesData = snapshot.val();
      console.log('Raw messages data:', messagesData); // Debug log
      
      if (messagesData) {
        const messagesList = [];
        
        // Process each message and convert file URLs if needed
        for (const [id, message] of Object.entries(messagesData)) {
          // Handle timestamp conversion
          let timestamp = message.timestamp;
          if (timestamp && typeof timestamp === 'object' && timestamp['.sv'] === 'timestamp') {
            // This is a server timestamp placeholder, use current time for sorting
            timestamp = Date.now();
          } else if (typeof timestamp === 'string') {
            // Convert string timestamp to number
            timestamp = parseInt(timestamp) || Date.now();
          } else if (!timestamp) {
            // No timestamp, use current time
            timestamp = Date.now();
          }
          
          // Convert file URL if it's a storage path
          let fileUrl = message.fileUrl || message.content?.mediaUrl;
          if (fileUrl && !fileUrl.startsWith('http')) {
            try {
              fileUrl = await getImageUrl(fileUrl);
              console.log('Converted file URL:', message.fileUrl, '->', fileUrl);
            } catch (error) {
              console.error('Error converting file URL:', error);
            }
          } else if (fileUrl) {
            console.log('Using existing download URL:', fileUrl);
          }
          
          messagesList.push({ 
            id, 
            ...message, 
            timestamp,
            fileUrl: fileUrl || message.fileUrl, // Use converted URL or fallback to original
            // Also preserve the content structure for compatibility
            content: {
              ...message.content,
              mediaUrl: fileUrl || message.content?.mediaUrl
            }
          });
        }
        
        // Sort messages
        messagesList.sort((a, b) => {
          // Sort by timestamp, fallback to id for consistent ordering
          const aTime = a.timestamp || 0;
          const bTime = b.timestamp || 0;
          if (aTime === bTime) {
            return a.id.localeCompare(b.id);
          }
          return aTime - bTime;
        });
        
        console.log('Processed messages:', messagesList); // Debug log
        setMessages(messagesList);
        
        // Mark messages as read
        const unreadCountRef = ref(database, `chatRooms/${selectedChat.id}/unreadCount/${currentUser.uid}`);
        set(unreadCountRef, 0);
      } else {
        setMessages([]);
      }
    });

    return () => off(messagesRef, 'value', unsubscribe);
  }, [selectedChat, currentUser]);

  // Monitor typing indicators
  useEffect(() => {
    if (!selectedChat || !currentUser) return;

    const typingRef = ref(database, `chats/${selectedChat.id}/typing`);
    const unsubscribe = onValue(typingRef, (snapshot) => {
      const typingData = snapshot.val();
      const typing = {};
      
      if (typingData) {
        Object.entries(typingData).forEach(([userId, isTyping]) => {
          if (userId !== currentUser.uid && isTyping) {
            typing[userId] = true;
          }
        });
      }
      
      setTypingUsers(typing);
    });

    return () => off(typingRef, 'value', unsubscribe);
  }, [selectedChat, currentUser]);

  // Send message
  const sendMessage = async (text, file = null) => {
    if ((!text.trim() && !file) || !selectedChat || sending) return;

    setSending(true);
    try {
      let fileUrl = null;
      let fileName = null;
      let fileType = null;

      // Upload file if provided
      if (file) {
        const fileRef = storageRef(storage, `chat_files/${selectedChat.id}/${Date.now()}_${file.name}`);
        const uploadResult = await uploadBytes(fileRef, file);
        fileUrl = await getDownloadURL(uploadResult.ref);
        fileName = file.name;
        fileType = file.type;
      }

      const messageData = {
        id: Date.now().toString(),
        senderId: currentUser.uid,
        content: {
          type: file ? (file.type.startsWith('image/') ? 'image' : 'file') : 'text',
          text: text.trim(),
          fileUrl,
          fileName,
          fileType
        },
        timestamp: Date.now(), // Use client timestamp for better consistency
        text: text.trim(), // For compatibility with rules
        fileUrl,
        fileName,
        fileType
      };

      // Add message to chat
      const messagesRef = ref(database, `chatRooms/${selectedChat.id}/messages`);
      await push(messagesRef, messageData);

      // Update last message in chat room
      const chatRoomRef = ref(database, `chatRooms/${selectedChat.id}`);
      await update(chatRoomRef, {
        lastMessage: text.trim() || (file ? `Sent a ${file.type.startsWith('image/') ? 'photo' : 'file'}` : ''),
        lastMessageTimestamp: Date.now()
      });

      // Update unread count for other participants
      const updates = {};
      Object.keys(selectedChat.participants).forEach(participantId => {
        if (participantId !== currentUser.uid) {
          updates[`unreadCount/${participantId}`] = (selectedChat.unreadCount?.[participantId] || 0) + 1;
        }
      });
      await update(chatRoomRef, updates);

      setMessageText('');
      
      // Clear typing indicator
      const typingRef = ref(database, `chats/${selectedChat.id}/typing/${currentUser.uid}`);
      set(typingRef, false);

      toast.success('Message sent!');
    } catch (error) {
      console.error('Error sending message:', error);
      toast.error('Failed to send message');
    } finally {
      setSending(false);
    }
  };

  // Handle typing
  const handleTyping = () => {
    if (!selectedChat) return;

    const typingRef = ref(database, `chats/${selectedChat.id}/typing/${currentUser.uid}`);
    set(typingRef, true);

    // Clear previous timeout
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }

    // Set timeout to stop typing indicator
    typingTimeoutRef.current = setTimeout(() => {
      set(typingRef, false);
    }, 2000);
  };

  // Handle file selection
  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) { // 5MB limit
        toast.error('File size must be less than 5MB');
        return;
      }
      sendMessage(messageText, file);
    }
  };

  // Handle input change
  const handleInputChange = (e) => {
    const newValue = e.target.value;
    console.log('Input change:', newValue); // Debug log
    setMessageText(newValue);
    handleTyping();
  };

  // Handle key press
  const handleKeyPress = (e) => {
    console.log('Key pressed:', e.key, 'Value:', messageText); // Debug log
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (messageText.trim()) {
        sendMessage(messageText);
      }
    } else {
      handleTyping();
    }
  };

  // Image modal
  const ImageModal = ({ src, onClose }) => {
    if (!src) return null;

    return (
      <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50" onClick={onClose}>
        <div className="max-w-4xl max-h-full p-4">
          <img src={src} alt="Full size" className="max-w-full max-h-full object-contain" />
        </div>
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-white hover:text-gray-300"
        >
          <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>
    );
  };

  // Handle new chat creation
  const handleChatCreated = (roomId) => {
    // Find the newly created chat and select it
    setTimeout(() => {
      const newChat = chats.find(chat => chat.id === roomId);
      if (newChat) {
        setSelectedChat(newChat);
      } else {
        // If chat not found in current list, refresh the chats
        window.location.reload();
      }
    }, 1000);
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#FF8C00]"></div>
      </div>
    );
  }

  return (
    <div className="flex-1 bg-slate-50 p-6">
      <div className="max-w-6xl mx-auto">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl font-bold text-gray-800">Messages</h1>
          <div className="flex items-center space-x-3">
            {/* Notification permission button */}
            {notificationPermission !== 'granted' && (
              <button
                onClick={requestNotificationPermission}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors flex items-center"
                title="Enable notifications"
              >
                <svg className="w-5 h-5 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                </svg>
                <span className="hidden sm:inline">Notifications</span>
              </button>
            )}
            
            {/* New message button */}
            <button
              onClick={() => setShowNewChatModal(true)}
              className="px-5 py-2 bg-[#FF8C00] text-white rounded-lg hover:bg-[#FF8C00]/90 transition-colors font-medium flex items-center"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clipRule="evenodd" />
              </svg>
              New Message
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" style={{ height: '600px' }}>
          {/* Chat List */}
          <div className="lg:col-span-1">
            <ChatList
              chats={chats}
              selectedChat={selectedChat}
              onSelectChat={setSelectedChat}
              currentUserId={currentUser.uid}
              onlineUsers={onlineUsers}
              userProfiles={userProfiles}
            />
          </div>

          {/* Chat Window */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-xl shadow-sm h-full flex flex-col">
              {/* Chat Header */}
              <div className="p-4 border-b border-gray-200 flex items-center justify-between">
                {selectedChat ? (
                  <div className="flex items-center space-x-3 flex-1">
                    <div className="relative">
                      <div className="w-12 h-12 rounded-full bg-gray-200 overflow-hidden">
                        {selectedChat.avatar ? (
                          <img src={selectedChat.avatar} alt={selectedChat.name} className="w-full h-full object-cover" />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-[#FF8C00] to-orange-600 text-white font-semibold">
                            {selectedChat.name?.charAt(0)?.toUpperCase() || '?'}
                          </div>
                        )}
                      </div>
                      {onlineUsers[selectedChat.otherUserId] && (
                        <div className="absolute -bottom-1 -right-1 w-3 h-3 bg-green-500 rounded-full border-2 border-white"></div>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center space-x-2">
                        <h2 className="font-semibold text-gray-800 truncate text-left">{selectedChat.name}</h2>
                        {userProfiles[selectedChat.otherUserId]?.userType && (
                          <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                            userProfiles[selectedChat.otherUserId]?.userType === 'Business' 
                              ? 'bg-blue-100 text-blue-800' 
                              : 'bg-green-100 text-green-800'
                          }`}>
                            {userProfiles[selectedChat.otherUserId]?.userType}
                          </span>
                        )}
                      </div>
                      
                      {/* Business Info Section */}
                      {userProfiles[selectedChat.otherUserId]?.business ? (
                        <div className="mt-1 flex items-center space-x-2">
                          <div className="w-6 h-6 rounded bg-orange-100 flex items-center justify-center flex-shrink-0">
                            <svg className="w-4 h-4 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                            </svg>
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-gray-700 truncate text-left">
                              {userProfiles[selectedChat.otherUserId].business.name}
                            </p>
                            {userProfiles[selectedChat.otherUserId].business.address && (
                              <p className="text-xs text-gray-500 truncate text-left">
                                📍 {formatAddress(userProfiles[selectedChat.otherUserId].business.address)}
                              </p>
                            )}
                          </div>
                        </div>
                      ) : (
                        <p className="text-sm text-gray-500">
                          {onlineUsers[selectedChat.otherUserId] ? (
                            <span className="flex items-center">
                              <div className="w-2 h-2 bg-green-500 rounded-full mr-1"></div>
                              Online
                            </span>
                          ) : 'Offline'}
                        </p>
                      )}
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center">
                      <svg className="w-6 h-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                      </svg>
                    </div>
                    <div>
                      <h2 className="font-semibold text-gray-800">Select a conversation</h2>
                      <p className="text-sm text-gray-500">Choose from the list to start chatting</p>
                    </div>
                  </div>
                )}
              </div>

              {/* Messages */}
              <div className="overflow-y-auto p-4 bg-slate-50" style={{ height: '500px' }}>
                {!selectedChat ? (
                  <div className="h-full flex items-center justify-center">
                    <div className="text-center text-gray-500">
                      <svg className="w-24 h-24 mx-auto mb-4 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                      </svg>
                      <h3 className="text-xl font-semibold mb-2">Start messaging</h3>
                      <p>Your conversations will appear here</p>
                    </div>
                  </div>
                ) : messages.length === 0 ? (
                  <div className="h-full flex items-center justify-center">
                    <div className="text-center text-gray-500">
                      <svg className="w-16 h-16 mx-auto mb-4 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                      </svg>
                      <p>Start the conversation!</p>
                    </div>
                  </div>
                ) : (
                  <>
                    {messages.map((message) => (
                      <Message
                        key={message.id}
                        message={message}
                        isOwn={message.senderId === currentUser.uid}
                        senderInfo={userProfiles[message.senderId]}
                        onImageClick={setSelectedImage}
                      />
                    ))}
                    
                    {/* Typing indicators */}
                    {Object.keys(typingUsers).length > 0 && (
                      <div className="flex justify-start mb-4">
                        <div className="bg-gray-200 rounded-lg px-4 py-2">
                          <div className="flex space-x-1">
                            <div className="w-2 h-2 bg-slate-500 rounded-full animate-bounce"></div>
                            <div className="w-2 h-2 bg-slate-500 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
                            <div className="w-2 h-2 bg-slate-500 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                          </div>
                        </div>
                      </div>
                    )}
                    
                    <div ref={messagesEndRef} />
                  </>
                )}
              </div>

              {/* Message Input */}
              <div className="p-4 border-t border-gray-200">
                {selectedChat ? (
                  <div className="flex items-end space-x-3">
                    <button
                      onClick={() => fileInputRef.current?.click()}
                      className="p-2 text-gray-500 hover:text-[#FF8C00] transition-colors"
                      title="Attach file"
                    >
                      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                      </svg>
                    </button>
                    
                    <div className="flex-1">
                      <textarea
                        value={messageText}
                        onChange={handleInputChange}
                        onKeyDown={handleKeyPress}
                        placeholder="Type a message..."
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-[#FF8C00] focus:border-transparent"
                        rows="1"
                        style={{ minHeight: '40px', maxHeight: '120px' }}
                      />
                    </div>
                    
                    <button
                      onClick={() => sendMessage(messageText)}
                      disabled={(!messageText.trim() && !fileInputRef.current?.files[0]) || sending}
                      className="p-2 bg-[#FF8C00] text-white rounded-lg hover:bg-[#FF8C00]/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {sending ? (
                        <div className="w-6 h-6 animate-spin rounded-full border-2 border-white border-t-transparent"></div>
                      ) : (
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                        </svg>
                      )}
                    </button>
                  </div>
                ) : (
                  <div className="flex items-start justify-start p-3">
                    <p className="text-gray-400 text-sm">Select a conversation to start messaging</p>
                  </div>
                )}
              </div>

              <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileSelect}
                accept="image/*,.pdf,.doc,.docx,.txt"
                className="hidden"
              />
            </div>
          </div>
        </div>

        {/* Image Modal */}
        <ImageModal src={selectedImage} onClose={() => setSelectedImage(null)} />
        
        {/* New Chat Modal */}
        <NewChatModal
          isOpen={showNewChatModal}
          onClose={() => setShowNewChatModal(false)}
          onChatCreated={handleChatCreated}
        />
      </div>
    </div>
  );
};

export default ChatDashboard; 